import { EmailIntakeState } from "generated-graphql/graphql";

/**
 * How each intake state reads in the grid and in its filter.
 *
 * Its own module because exporting constants alongside a component breaks fast refresh
 * for the whole file, and both the column and its enum filter need these labels.
 */
export const INTAKE_STATE_DISPLAY: Record<
  EmailIntakeState,
  { label: string; color: "success" | "default" | "error" }
> = {
  [EmailIntakeState.On]: { label: "On", color: "success" },
  [EmailIntakeState.Paused]: { label: "Paused", color: "default" },
  [EmailIntakeState.OrganizationDisabled]: { label: "Org disabled", color: "error" },
};

/**
 * The Match filter's options, mirroring what the column actually displays.
 *
 * Three, not the enum's two: the label combines `matchType` with
 * `includeSubdomains`, so a grid showing "Domain + subdomains" against a filter
 * offering only "Domain" left that third kind unreachable. `DOMAIN` here means an
 * exact domain, which is what the label says.
 */
export const MATCH_FILTER_VALUES = ["ADDRESS", "DOMAIN", "DOMAIN_WITH_SUBDOMAINS"] as const;

export const MATCH_FILTER_LABELS: Record<string, string> = {
  ADDRESS: "Address",
  DOMAIN: "Domain",
  DOMAIN_WITH_SUBDOMAINS: "Domain + subdomains",
};

/**
 * How the Conflicts cell explains itself.
 *
 * Two kinds reach here and they need different words. A same-value conflict is another
 * organization holding the identical entry. An overlapping one is another organization's
 * *different* value whose subdomain scope nests with this one — so "also claimed by"
 * would describe something it does not hold, and the value it does hold is the useful
 * part.
 */
export function conflictTooltip(
  conflicts: { tenantName: string; value: string; kind: string }[]
): string {
  const lines = conflicts.map((conflict) =>
    conflict.kind === "OVERLAPPING_SUBDOMAIN"
      ? `${conflict.tenantName} — overlapping scope, ${conflict.value}`
      : `${conflict.tenantName} — same value`
  );
  return `${lines.join("; ")}. Mail matching this entry resolves to more than one organization, so it is held for a person to route until one entry is removed or switched off.`;
}
